import { useEffect, useState } from "react";
import moment from "moment";
import * as Sentry from "@sentry/react";
import analyticsDashboardApi from "../../../../services/analyticsDashboard.service";
import useSedeMode from "../../../../hooks/useSedeMode";
import usePermission from "../../../../hooks/usePermission";
import { ANALYTICS_IMPLEMENTED_PERMISSIONS_LIST } from "../../../../const/analyticsPermissions";

export const SEDE_ANALYTICS_ACCESS = {
  // The dashboard can be used: outside a sede always (nothing is asked), in
  // a sede once the analytics service answered the probe.
  AVAILABLE: "available",
  // Sede tab, the probe has not been answered yet.
  CHECKING: "checking",
  // Sede tab, the probe was not answered: the service refused the credential,
  // could not be reached or failed, or there was no company to ask for.
  UNAVAILABLE: "unavailable",
  // Sede tab, the user holds none of the widget permissions: nothing is
  // asked, and the dashboard would only show its "no permission" texts.
  NOT_PERMITTED: "notPermitted",
};

const { AVAILABLE, CHECKING, UNAVAILABLE, NOT_PERMITTED } = SEDE_ANALYTICS_ACCESS;

// What the guards of the analytics service answer to a credential they do
// not accept for the requested company (JWT guard: 401; tenant guard: 403).
const REFUSED_HTTP_CODES = [401, 403];

// Why this sede tab is not offered the dashboard, for a later report. Only a
// breadcrumb: a refusal is the expected outcome while the service does not
// accept the credential, and the analytics client and the token module
// already report their own unexpected failures.
const recordUnanswered = (error) => {
  const refused = REFUSED_HTTP_CODES.includes(error?.httpCode);
  try {
    Sentry.addBreadcrumb({
      category: "partnerAccess",
      message: refused ? "sede.analyticsRefused" : "sede.analyticsProbeFailed",
      level: refused ? "info" : "warning",
      data: { httpCode: error?.httpCode, internalCode: error?.internalCode },
    });
  } catch (e) {
    // Telemetry must never decide what the tab shows.
  }
};

/**
 * Whether the advanced analytics dashboard can be used from this tab.
 *
 * Partner access: in a sede tab the dashboard authenticates with the sede's
 * delegated token, and the analytics service may not accept it, or may not be
 * reachable from this origin. A few of its endpoints are not guarded and
 * would answer anyway, leaving a dashboard that is only half filled. So a
 * sede tab makes ONE guarded, company-scoped call and decides from it: only a
 * successful answer makes the dashboard usable. A refusal (401 or 403), no
 * answer at all (network, CORS, timeout) and any other error all mean it is
 * not. There is no "sede" switch here: the day the service accepts the
 * credential the same call succeeds and the dashboard is offered.
 *
 * The dashboard gates every widget on the analytics widget permissions. A
 * sede user who holds none of them is `NOT_PERMITTED` and nothing is asked.
 *
 * Outside sede mode it is `AVAILABLE` from the first render and no request is
 * made. The check runs once per mount: call this hook once per screen and
 * hand its answer down.
 */
const useSedeAnalyticsAccess = (companyId) => {
  const sedeMode = useSedeMode();
  const { hasAnyPermission } = usePermission();
  const holdsWidgetPermission = hasAnyPermission(ANALYTICS_IMPLEMENTED_PERMISSIONS_LIST);
  const shouldProbe = sedeMode && holdsWidgetPermission && !!companyId;
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    if (!shouldProbe) return undefined;

    let cancelled = false;
    // A light guarded call: the lead sources of the last day. The service
    // wants the start strictly before the end, and not in the future.
    const endDate = moment().format("YYYY-MM-DD");
    const startDate = moment().subtract(1, "day").format("YYYY-MM-DD");
    analyticsDashboardApi
      .getAvailableLeadSources({ companyId, startDate, endDate })
      .then(
        () => AVAILABLE,
        (error) => {
          recordUnanswered(error);
          return UNAVAILABLE;
        }
      )
      .then((access) => {
        if (!cancelled) setAnswer({ companyId, access });
      });

    return () => {
      cancelled = true;
    };
  }, [shouldProbe, companyId]);

  if (!sedeMode) return AVAILABLE;
  if (!holdsWidgetPermission) return NOT_PERMITTED;
  if (!companyId) return UNAVAILABLE;
  // An answer only counts for the company it was asked for.
  return answer !== null && answer.companyId === companyId ? answer.access : CHECKING;
};

export default useSedeAnalyticsAccess;
